import { RuleMultiplicityEnum } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { ColorEnum, IconEnum } from '@e-socna/data-layer';

import type { WorkflowStateLookup } from './build-workflow-graph.util';

export type Gender = 'feminine' | 'masculine';

export interface CompatibleStateEntry {
  readonly id: string;
  readonly label: string;
  readonly color?: ColorEnum;
  readonly icon?: IconEnum;
}

export interface CompatibleStateGroup {
  readonly entityLabel: string;
  readonly gender: Gender;
  readonly multiplicity: RuleMultiplicityEnum;
  readonly states: readonly CompatibleStateEntry[];
}

export interface CompatibleFieldConfig {
  readonly field: string;
  readonly entityLabel: string;
  readonly gender: Gender;
  readonly getState: WorkflowStateLookup;
}

const MULTIPLICITY_LABELS_FR: Record<
  RuleMultiplicityEnum,
  Record<Gender, string>
> = {
  [RuleMultiplicityEnum.All]: { masculine: 'tous', feminine: 'toutes' },
  [RuleMultiplicityEnum.AnyNumber]: {
    masculine: "n'importe quel nombre",
    feminine: "n'importe quel nombre",
  },
  [RuleMultiplicityEnum.AtLeastOne]: {
    masculine: 'au moins un',
    feminine: 'au moins une',
  },
  [RuleMultiplicityEnum.AtMostOne]: {
    masculine: 'au plus un',
    feminine: 'au plus une',
  },
  [RuleMultiplicityEnum.OneAndOnlyOne]: {
    masculine: 'un et un seul',
    feminine: 'une et une seule',
  },
  [RuleMultiplicityEnum.Never]: { masculine: 'aucun', feminine: 'aucune' },
};

export const translateMultiplicity = (
  multiplicity: RuleMultiplicityEnum,
  gender: Gender,
): string => MULTIPLICITY_LABELS_FR[multiplicity][gender];

interface CompatibilityRuleSource {
  readonly multiplicity: RuleMultiplicityEnum;
  readonly stateIds?: readonly unknown[] | null;
}

const isCompatibilityRule = (
  value: unknown,
): value is CompatibilityRuleSource =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Partial<CompatibilityRuleSource>).multiplicity === 'string';

export const extractCompatibleStateGroups = (
  raw: unknown,
  configs: readonly CompatibleFieldConfig[],
): readonly CompatibleStateGroup[] => {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ARRAY;
  }

  const { compatibilities } = raw as Record<string, unknown>;

  if (typeof compatibilities !== 'object' || compatibilities === null) {
    return EMPTY_ARRAY;
  }

  const compatibilitiesRecord = compatibilities as Record<string, unknown>;

  return configs.flatMap(
    ({
      field,
      entityLabel,
      gender,
      getState,
    }): readonly CompatibleStateGroup[] => {
      const rule = compatibilitiesRecord[field];

      if (!isCompatibilityRule(rule) || !rule.stateIds?.length) {
        return EMPTY_ARRAY;
      }

      const states = rule.stateIds.map((id) => {
        const stateId = String(id);
        const state = getState(stateId);

        return {
          id: stateId,
          label: state?.label ?? stateId,
          color: state?.chip.color,
          icon: state?.chip.icon,
        };
      });

      return [{ entityLabel, gender, multiplicity: rule.multiplicity, states }];
    },
  );
};
